import type {
  MobileFrontEndBubbleMessageView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { humanize } from './columns';
import { COMPOSITE_THREAD_KEY_SEPARATOR } from './constants';
import { artifactTableView } from './labels';
import { displayableMediaUrl, resolveMediaKind, type GalleryMediaKind } from './media-kind';

/** Resolved bubble direction: outgoing (owner-sent, right) or incoming (left). */
export type MessageDirection = 'outgoing' | 'incoming';

/** One file shown inside a message bubble. */
export interface MappedAttachment {
  url: string;
  kind: GalleryMediaKind;
  filename: string | null;
}

/** A single message ready to render as a bubble. */
export interface MappedMessage {
  id: string;
  body: string;
  timestamp: string | null;
  direction: MessageDirection;
  senderLabel: string | null;
  attachments: MappedAttachment[];
  /** Original row, for the detail popover. */
  row: Record<string, unknown>;
}

/** One selectable conversation thread in the left pane. */
export interface MessageThread {
  /** Stable thread key (thread field value, or parent artifact id as string). */
  key: string;
  title: string;
  subtitle: string | null;
  /** Optional recency hint used to order the thread list. */
  lastActivity?: string | null;
  /** Optional message count (known for client-grouped by_field threads). */
  count?: number;
}

/**
 * Overlay exact groupBy totals onto threads.
 *
 * A missing map entry keeps the windowed ``count`` already attached to
 * ``by_field`` threads. That entry is absent while counts are still loading,
 * when the thread fields are not groupable (the query is skipped), and when
 * the conversation falls outside the groupBy limit.
 */
export function applyExactThreadCounts(
  threads: MessageThread[],
  threadCounts: Record<string, number> | undefined,
): MessageThread[] {
  return threads.map((thread) => ({
    ...thread,
    count: threadCounts?.[thread.key] ?? thread.count,
  }));
}

/** Resolve a possibly dotted GraphQL path (e.g. ``fromParty.isPhoneOwner``) on a row. */
export function resolvePath(row: Record<string, unknown> | null | undefined, path: string): unknown {
  if (!row || !path) return undefined;
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function asDisplayString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function asScalarString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

function looksLikePhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || looksLikeUuid(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return false;
  return /^[+\d][\d\s()./-]*$/.test(trimmed);
}

function participantLabelRank(value: string): number {
  if (!value) return 0;
  if (!looksLikePhone(value) && !looksLikeUuid(value)) return 3;
  if (looksLikePhone(value)) return 2;
  return 1;
}

function betterParticipantLabel(current: string, next: string): string {
  return participantLabelRank(next) > participantLabelRank(current) ? next : current;
}

const PARTY_NAME_FIELDS = ['name', 'displayName', 'fullName'];
const PARTY_PHONE_FIELDS = ['phoneNumber', 'phone', 'phoneNumbers', 'number'];
const PARTY_ID_FIELDS = ['identifier', 'value', 'id'];
const PARTY_OWNER_FIELDS = ['isPhoneOwner', 'isOwner', 'isDeviceOwner'];
const PARTY_LIST_FIELDS = ['parties', 'participants', 'members'];

/** Vendor dumps often name the device owner ``Local User`` instead of setting an owner flag. */
function labelLooksLikeLocalUser(value: string): boolean {
  return /local user/i.test(value);
}

/** True when a party object or scalar label is the device / local user. */
export function isLocalParty(entry: unknown): boolean {
  if (entry == null) return false;
  if (typeof entry !== 'object') return labelLooksLikeLocalUser(String(entry));
  const record = entry as Record<string, unknown>;
  if (PARTY_OWNER_FIELDS.some((field) => record[field] === true)) return true;
  for (const field of PARTY_NAME_FIELDS) {
    if (labelLooksLikeLocalUser(asScalarString(record[field]))) return true;
  }
  return labelLooksLikeLocalUser(partyDisplayLabel(entry));
}

function senderLooksLikeLocalUser(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): boolean {
  const party = senderPartyFromRow(row, view);
  if (party != null && isLocalParty(party)) return true;
  const name = view.sender_name_field ? asScalarString(resolvePath(row, view.sender_name_field)) : '';
  if (labelLooksLikeLocalUser(name)) return true;
  return labelLooksLikeLocalUser(senderDisplayLabel(row, view));
}

/**
 * Best human label for a party: a name if present, else a phone, else an
 * identifier (including a UUID as a last resort).
 */
export function partyDisplayLabel(entry: unknown): string {
  if (entry == null) return '';
  if (typeof entry !== 'object') return String(entry).trim();
  const record = entry as Record<string, unknown>;

  for (const field of PARTY_NAME_FIELDS) {
    const name = asScalarString(record[field]);
    if (name && !looksLikeUuid(name)) return name;
  }

  for (const field of PARTY_PHONE_FIELDS) {
    const raw = record[field];
    const candidates = Array.isArray(raw) ? raw : [raw];
    for (const candidate of candidates) {
      const phone = asScalarString(candidate);
      if (phone && looksLikePhone(phone)) return phone;
    }
  }

  for (const field of PARTY_ID_FIELDS) {
    const id = asScalarString(record[field]);
    if (id && looksLikePhone(id)) return id;
  }

  for (const field of [...PARTY_ID_FIELDS, ...PARTY_NAME_FIELDS]) {
    const fallback = asScalarString(record[field]);
    if (fallback) return fallback;
  }
  return '';
}

function partyObjectPath(fieldPath: string | undefined): string | null {
  if (!fieldPath?.includes('.')) return null;
  return fieldPath.split('.').slice(0, -1).join('.');
}

function counterpartRootName(fromRoot: string): string | null {
  if (!/^from/i.test(fromRoot)) return null;
  const toRoot = fromRoot.replace(/^from/i, 'to');
  return toRoot === fromRoot ? null : toRoot;
}

/** Prefer name, then a phone-like id, then any other identifier. */
export function preferParticipantLabel(name: string, identifier: string): string {
  const trimmedName = name.trim();
  const trimmedId = identifier.trim();
  if (trimmedName && !looksLikeUuid(trimmedName)) return trimmedName;
  if (trimmedId && looksLikePhone(trimmedId)) return trimmedId;
  return trimmedName || trimmedId;
}

function senderPartyFromRow(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): unknown {
  const path = partyObjectPath(view.sender_name_field) ?? partyObjectPath(view.sender_id_field);
  return path ? resolvePath(row, path) : undefined;
}

function counterpartPartyFromRow(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): unknown {
  const senderPath = view.sender_name_field ?? view.sender_id_field;
  const fromRoot = senderPath?.split('.')[0];
  if (!fromRoot) return undefined;
  const toRoot = counterpartRootName(fromRoot);
  return toRoot ? row[toRoot] : undefined;
}

function senderDisplayLabel(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): string {
  const party = senderPartyFromRow(row, view);
  if (party && typeof party === 'object') {
    const fromParty = partyDisplayLabel(party);
    if (fromParty) return fromParty;
  }
  const name = view.sender_name_field ? asScalarString(resolvePath(row, view.sender_name_field)) : '';
  const id = view.sender_id_field ? asScalarString(resolvePath(row, view.sender_id_field)) : '';
  return preferParticipantLabel(name, id);
}

function addPartyParticipant(
  seen: Map<string, string>,
  entry: unknown,
): void {
  if (entry == null || isLocalParty(entry)) return;
  const label = partyDisplayLabel(entry);
  if (!label) return;
  const record = entry !== null && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
  const id =
    (record && (asScalarString(record.identifier) || asScalarString(record.id))) || label;
  const existing = seen.get(id);
  seen.set(id, existing ? betterParticipantLabel(existing, label) : label);
}

/**
 * Participants in a message row other than the local user, labeled name >
 * phone > identifier.
 */
export function participantsFromMessageRow(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): string[] {
  const seen = new Map<string, string>();
  const direction = resolveDirection(row, view);

  if (direction === 'incoming') {
    const party = senderPartyFromRow(row, view);
    if (party && typeof party === 'object') {
      addPartyParticipant(seen, party);
    } else {
      const label = senderDisplayLabel(row, view);
      const id = view.sender_id_field
        ? asScalarString(resolvePath(row, view.sender_id_field)) || label
        : label;
      if (label && !labelLooksLikeLocalUser(label)) {
        const existing = seen.get(id);
        seen.set(id, existing ? betterParticipantLabel(existing, label) : label);
      }
    }
  } else {
    const counterpart = counterpartPartyFromRow(row, view);
    if (Array.isArray(counterpart)) {
      for (const entry of counterpart) addPartyParticipant(seen, entry);
    } else {
      addPartyParticipant(seen, counterpart);
    }
  }

  for (const field of PARTY_LIST_FIELDS) {
    const list = row[field];
    if (!Array.isArray(list)) continue;
    for (const entry of list) addPartyParticipant(seen, entry);
  }

  return [...seen.values()];
}

/** Participants from a parent chat's party list, excluding the local user. */
export function participantLabelsFromPartyList(value: unknown): string[] {
  const entries = Array.isArray(value) ? value : value == null ? [] : [value];
  const seen = new Map<string, string>();
  for (const entry of entries) addPartyParticipant(seen, entry);
  return [...seen.values()];
}

function isGenericThreadTitle(title: string, key: string): boolean {
  const trimmed = title.trim();
  if (!trimmed) return true;
  if (trimmed === key) return true;
  if (trimmed === `Thread ${key}`) return true;
  return looksLikeUuid(trimmed);
}

/**
 * Title for the thread list: a real chat name if authored, otherwise the
 * other participants (name > phone > id). Thread ids are the last resort.
 */
export function resolveConversationTitle(
  key: string,
  configuredTitle: string,
  participantLabels: Iterable<string>,
): string {
  const configured = configuredTitle.trim();
  const participants = [
    ...new Set(
      [...participantLabels]
        .map((label) => label.trim())
        .filter((label) => label && !labelLooksLikeLocalUser(label)),
    ),
  ];

  if (configured && !isGenericThreadTitle(configured, key)) {
    if (looksLikePhone(configured)) {
      const names = participants.filter((label) => participantLabelRank(label) === 3);
      if (names.length > 0) return names.join(', ');
    }
    return configured;
  }
  if (participants.length > 0) return participants.join(', ');
  return fallbackThreadTitle(key);
}

/** Merge newly seen participant labels, keeping the richer label for a duplicate. */
export function mergeParticipantLabels(current: string[], incoming: Iterable<string>): string[] {
  const seen = new Map<string, string>();
  for (const label of [...current, ...incoming]) {
    const trimmed = label.trim();
    if (!trimmed || labelLooksLikeLocalUser(trimmed)) continue;
    const key = trimmed.toLowerCase();
    const existing = seen.get(key);
    seen.set(key, existing ? betterParticipantLabel(existing, trimmed) : trimmed);
  }
  return [...seen.values()];
}

/**
 * Compute whether a message is outgoing (phone owner, right-hand bubble).
 *
 * A sender whose name contains ``local user`` is treated as the device owner
 * even when the view's owner flag is missing or false.
 */
export function resolveDirection(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): MessageDirection {
  if (senderLooksLikeLocalUser(row, view)) return 'outgoing';

  const direction = view.direction;
  if (!direction) return 'incoming';

  if (direction.owner_field) {
    return resolvePath(row, direction.owner_field) ? 'outgoing' : 'incoming';
  }
  if (direction.field) {
    const raw = resolvePath(row, direction.field);
    if (raw == null) return 'incoming';
    const value = String(raw).trim().toLowerCase();
    const outgoing = (direction.outgoing_values ?? []).some(
      (candidate) => candidate.trim().toLowerCase() === value,
    );
    return outgoing ? 'outgoing' : 'incoming';
  }
  return 'incoming';
}

/** Fields that make up a ``by_field`` thread key (single ``key_field`` or composite ``key_fields``). */
export function threadKeyFields(view: MobileFrontEndBubbleMessageView): string[] {
  const thread = view.thread;
  if (thread.mode !== 'by_field') return [];
  if (thread.key_fields && thread.key_fields.length > 0) return thread.key_fields;
  return thread.key_field ? [thread.key_field] : [];
}

/** Whether the ``by_field`` thread key is derived from multiple fields (client-side only). */
export function isCompositeThreadKey(view: MobileFrontEndBubbleMessageView): boolean {
  return view.thread.mode === 'by_field' && (view.thread.key_fields?.length ?? 0) > 0;
}

/**
 * Thread key for a message: the ``by_parent`` link value, the single ``by_field``
 * key, or an order-independent combination of ``key_fields`` (so e.g. a message
 * and its reply group together regardless of sender/recipient direction).
 */
export function resolveThreadKey(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): string | null {
  const thread = view.thread;
  if (thread.mode === 'by_parent') {
    if (!thread.parent_link_field) return null;
    const value = resolvePath(row, thread.parent_link_field);
    if (value == null || value === '') return null;
    return String(value);
  }

  const fields = threadKeyFields(view);
  if (fields.length === 0) return null;
  const parts = fields
    .map((field) => resolvePath(row, field))
    .filter((value) => value != null && value !== '')
    .map((value) => String(value).trim())
    .filter((value) => value !== '');
  if (parts.length === 0) return null;
  // Sort so the same participants yield one key regardless of direction/order.
  return parts.sort().join(COMPOSITE_THREAD_KEY_SEPARATOR);
}

const UNGROUPED_THREAD_KEY = '\u0000ungrouped';

/** Hydrate rows already grouped into a selectable conversation. */
export interface CitedMessageGroup {
  thread: MessageThread;
  messages: MappedMessage[];
}

function configuredThreadLabels(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): { title: string; subtitle: string | null } {
  const thread = view.thread;
  const titlePath = thread.mode === 'by_parent' ? thread.parent_title_field : thread.title_field;
  const subtitlePath =
    thread.mode === 'by_parent' ? thread.parent_subtitle_field : thread.subtitle_field;
  const title = titlePath ? asDisplayString(resolvePath(row, titlePath)).trim() : '';
  const subtitle = subtitlePath ? asDisplayString(resolvePath(row, subtitlePath)).trim() : '';
  return { title, subtitle: subtitle || null };
}

function fallbackThreadTitle(key: string): string {
  if (key === UNGROUPED_THREAD_KEY) return 'Other messages';
  return key.split(COMPOSITE_THREAD_KEY_SEPARATOR).filter(Boolean).join(' · ') || 'Conversation';
}

/**
 * Group hydrated message rows with the same thread-key rules as the viewer.
 *
 * Rows keep their incoming order (hydrate already sorts them). Messages with no
 * resolvable thread key land in a fallback group rather than disappearing.
 */
export function groupHydratedMessageRows(
  rows: Record<string, unknown>[],
  view: MobileFrontEndBubbleMessageView,
  idField: string,
): CitedMessageGroup[] {
  const groups = new Map<
    string,
    CitedMessageGroup & { configuredTitle: string; participants: string[] }
  >();
  const order: string[] = [];

  for (const row of rows) {
    const message = mapMessage(row, view, idField);
    const resolved = resolveThreadKey(row, view);
    const key = resolved && resolved !== '' ? resolved : UNGROUPED_THREAD_KEY;
    const labels = configuredThreadLabels(row, view);
    const participants = participantsFromMessageRow(row, view);
    const existing = groups.get(key);
    if (existing) {
      existing.messages.push(message);
      existing.thread.count = (existing.thread.count ?? 0) + 1;
      existing.participants = mergeParticipantLabels(existing.participants, participants);
      if (!existing.configuredTitle && labels.title) existing.configuredTitle = labels.title;
      if (!existing.thread.subtitle && labels.subtitle) existing.thread.subtitle = labels.subtitle;
      continue;
    }
    groups.set(key, {
      configuredTitle: labels.title,
      participants,
      thread: {
        key,
        title: resolveConversationTitle(key, labels.title, participants),
        subtitle: labels.subtitle,
        lastActivity: message.timestamp,
        count: 1,
      },
      messages: [message],
    });
    order.push(key);
  }

  return order.map((key) => {
    const group = groups.get(key);
    if (group == null) {
      throw new Error(`grouped thread ${key} is missing`);
    }
    return {
      thread: {
        ...group.thread,
        title: resolveConversationTitle(key, group.configuredTitle, group.participants),
      },
      messages: group.messages,
    };
  });
}

/** Map a raw GraphQL message row to a render-ready bubble. */
export function mapMessage(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
  idField: string,
): MappedMessage {
  const timestampRaw = resolvePath(row, view.timestamp_field);
  const senderLabel = senderDisplayLabel(row, view);

  return {
    id: String(resolvePath(row, idField) ?? asDisplayString(resolvePath(row, view.timestamp_field))),
    body: asDisplayString(resolvePath(row, view.body_field)),
    timestamp: timestampRaw == null ? null : String(timestampRaw),
    direction: resolveDirection(row, view),
    senderLabel: senderLabel || null,
    attachments: mapMessageAttachments(row, view),
    row,
  };
}

/**
 * Files the bubble should preview.
 *
 * Each configured source is an object or a list. Device-local paths are
 * dropped, and two sources that resolve to the same URL (Magnet often stores
 * both ``attachment`` and ``localMediaPath``) collapse to one preview.
 */
export function mapMessageAttachments(
  row: Record<string, unknown>,
  view: MobileFrontEndBubbleMessageView,
): MappedAttachment[] {
  const attachments: MappedAttachment[] = [];
  const seen = new Set<string>();

  for (const source of view.attachments ?? []) {
    const container = resolvePath(row, source.items_field);
    const items = Array.isArray(container) ? container : container == null ? [] : [container];
    for (const item of items) {
      if (item == null || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const url = displayableMediaUrl(resolvePath(record, source.media_field));
      if (!url || seen.has(url)) continue;
      seen.add(url);
      const mime = source.mime_field
        ? scalarString(resolvePath(record, source.mime_field))
        : null;
      const filenameRaw = source.filename_field
        ? scalarString(resolvePath(record, source.filename_field))
        : null;
      attachments.push({
        url,
        kind: resolveMediaKind('auto', mime, filenameRaw ?? url),
        filename: attachmentFilename(filenameRaw, url),
      });
    }
  }

  return attachments;
}

function scalarString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text === '' ? null : text;
}

function attachmentFilename(filename: string | null, url: string): string | null {
  const source = filename || url;
  const path = source.split(/[?#]/)[0];
  const name = path.split('/').pop() ?? '';
  return name || null;
}

/** One field/value entry shown in the message detail popover. */
export interface MessageDetailEntry {
  field: string;
  label: string;
  value: unknown;
}

/**
 * True when a (possibly dotted) GraphQL path can be selected on *artifact*.
 * Nested paths only require the top-level parent to exist as an object field.
 */
export function isSelectableFieldPath(
  artifact: MobileUiConfigArtifact | undefined,
  path: string,
): boolean {
  if (!path || !artifact?.fields?.length) return false;
  const parent = path.split('.')[0];
  return artifact.fields.some((field) => field.name === parent);
}

/**
 * Fields to show in the message detail popover: the view's ``detail_fields``
 * (in order) or, when empty, every scalar field on the artifact.
 *
 * Authored ``detail_fields`` are filtered against introspected artifact fields
 * so a stale/mismatched name (e.g. domain ``type`` vs GraphQL ``messageType``)
 * cannot break the whole thread-messages GraphQL query.
 */
export function detailFieldNames(
  view: MobileFrontEndBubbleMessageView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  if (view.detail_fields && view.detail_fields.length > 0) {
    return view.detail_fields.filter((field) => isSelectableFieldPath(artifact, field));
  }
  return (artifact?.fields ?? [])
    .filter((f) => f.scalar && !f.list && !f.object)
    .map((f) => f.name);
}

/** Resolve popover entries (label + value) for a message row. */
export function messageDetailEntries(
  row: Record<string, unknown>,
  fieldNames: string[],
): MessageDetailEntry[] {
  return fieldNames.map((field) => ({
    field,
    label: humanize(field.split('.').pop() ?? field),
    value: resolvePath(row, field),
  }));
}

function configuredPartyLeafPaths(
  view: MobileFrontEndBubbleMessageView,
  root: string,
): string[] {
  const leaves = new Set<string>();
  const nameLeaf = view.sender_name_field?.split('.').pop();
  const idLeaf = view.sender_id_field?.split('.').pop();
  const ownerLeaf = view.direction?.owner_field?.split('.').pop();
  if (nameLeaf) leaves.add(nameLeaf);
  if (idLeaf) leaves.add(idLeaf);
  if (ownerLeaf) leaves.add(ownerLeaf);
  return [...leaves].map((leaf) => `${root}.${leaf}`);
}

/**
 * Extra GraphQL paths needed to label thread participants (sender party,
 * counterpart ``toParty``, direction). Counterpart leaves mirror the configured
 * sender party so the document only names fields the schema already serves.
 */
export function threadParticipantSelectionFields(
  view: MobileFrontEndBubbleMessageView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const fields: string[] = [];
  if (view.sender_name_field) fields.push(view.sender_name_field);
  if (view.sender_id_field) fields.push(view.sender_id_field);
  if (view.direction?.owner_field) fields.push(view.direction.owner_field);
  if (view.direction?.field) fields.push(view.direction.field);

  const senderRoot = (view.sender_name_field ?? view.sender_id_field)?.split('.')[0];
  if (senderRoot) {
    const toRoot = counterpartRootName(senderRoot);
    const toField = artifact?.fields.find((field) => field.name === toRoot);
    if (toRoot && toField && (toField.object || toField.list)) {
      fields.push(...configuredPartyLeafPaths(view, toRoot));
    }
  }

  return [...new Set(fields)];
}

function parentParticipantListField(
  parent: MobileUiConfigArtifact | undefined,
): string | undefined {
  if (!parent) return undefined;
  const table = artifactTableView(parent);
  const partyColumn = table?.column_overrides?.find((override) => override.render === 'party_list');
  if (partyColumn?.field) return partyColumn.field;
  const nestedParty = table?.nested?.find((nested) =>
    /party|participant|member/i.test(nested.field),
  );
  if (nestedParty?.field) return nestedParty.field;
  return parent.fields.find((field) => field.list && PARTY_LIST_FIELDS.includes(field.name))?.name;
}

/**
 * Nested party-list paths on a parent (chat) artifact, only when the child
 * type is known so the selection cannot name a missing field.
 */
export function parentPartySelectionFields(
  parent: MobileUiConfigArtifact | undefined,
  uiConfig: MobileUiConfigResponse,
): { listField: string; paths: string[] } | null {
  const listField = parentParticipantListField(parent);
  if (!listField || !parent) return null;
  const nested = artifactTableView(parent)?.nested?.find((entry) => entry.field === listField);
  const child = nested?.graphql_type ? uiConfig.artifacts[nested.graphql_type] : undefined;
  if (!child?.fields.length) return null;
  const childNames = new Set(child.fields.map((field) => field.name));
  const wanted = [
    ...PARTY_NAME_FIELDS,
    ...PARTY_PHONE_FIELDS,
    ...PARTY_ID_FIELDS,
    ...PARTY_OWNER_FIELDS,
  ];
  const paths = wanted.filter((name) => childNames.has(name)).map((name) => `${listField}.${name}`);
  return paths.length > 0 ? { listField, paths } : null;
}
